import { useEffect, useMemo, useState } from 'react';
/* eslint-disable no-await-in-loop */

import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { addQueueItem } from '../idb';

export default function PortalTasks({ user }) {
 const [tasks, setTasks] = useState([]);
 const [trades, setTrades] = useState([]);
 const [unitsById, setUnitsById] = useState({});
 const [buildingsById, setBuildingsById] = useState({});
 const [selectedTask, setSelectedTask] = useState(null);
 const [dialogOpen, setDialogOpen] = useState(false);
 const [progressData, setProgressData] = useState({
 pct_complete: 0,
 qty_done: '',
 notes: ''
 });

 useEffect(() => {
 loadData();
 }, []);

 const loadData = async () => {
 try {
 const annotateNeedsLog = async (rawTasks) => {
 const sevenDaysAgoIso = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
 const tasksWith = [];
 for (const t of rawTasks) {
 try {
 // Find unit to derive project_id
 const unit = unitsById[t.unit_id] || (await axios.get(`/units/${t.unit_id}`)).data;
 const projectId = (() => {
 // find building then project_id from building
 const b = Object.values(buildingsById).find((bi) => bi.id === unit.building_id);
 return b?.project_id || null;
 })();
 let needsLog = true;
 if (projectId) {
 const res = await axios.get(`/cycle3/progress-logs?project_id=${projectId}&unit_id=${t.unit_id}`);
 const verified = (res.data || []).filter((l) => l.status === 'verified' && l.submitted_at >= sevenDaysAgoIso);
 needsLog = verified.length === 0;
 }
 tasksWith.push({ ...t, needsLog });
 } catch {
 tasksWith.push({ ...t, needsLog: false });
 }
 }
 // Sort: needs log first
 tasksWith.sort((a, b) => (b.needsLog ? 1 : 0) - (a.needsLog ? 1 : 0));
 return tasksWith;
 };

 const [tasksRes, tradesRes] = await Promise.all([
 axios.get('/tasks'),
 axios.get('/trades'),
 ]);
 const annotated = await annotateNeedsLog(tasksRes.data);
 setTasks(annotated);
 setTrades(tradesRes.data);
 // Preload units and buildings to compute project_id for logs when needed
 const unitIds = Array.from(new Set(tasksRes.data.map(t => t.unit_id)));
 const unitsMap = {};
 const buildingsMap = {};
 for (const uid of unitIds) {
 const u = await axios.get(`/units/${uid}`);
 unitsMap[uid] = u.data;
 // find building for this unit
 const bList = await axios.get(`/buildings`);
 const b = (bList.data || []).find(bi => bi.id === u.data.building_id) || null;
 if (b) buildingsMap[b.id] = b;
 }
 setUnitsById(unitsMap);
 setBuildingsById(buildingsMap);
 } catch (e) {
 toast.error('Failed to load tasks');
 }
 };

 const openTaskDialog = (task) => {
 setSelectedTask(task);
 setProgressData({ pct_complete: 0, qty_done: '', notes: '' });
 setDialogOpen(true);
 };

 const submitProgress = async () => {
 if (!selectedTask) return;
 
 const isOffline = !navigator.onLine;
 const payload = { 
 task_id: selectedTask.id, 
 pct_complete: parseInt(progressData.pct_complete), 
 qty_done: progressData.qty_done, 
 notes: progressData.notes
 };
 
 if (isOffline) {
 const item = { id: 'q-' + crypto.randomUUID(), type: 'progress_log_v1', status: 'queued', createdAt: Date.now(), payload };
 await addQueueItem(item);
 toast.success('Queued: Progress logged (will sync when online)');
 } else {
 try {
 await axios.post('/progress-logs', payload);
 toast.success('Progress logged successfully');
 } catch (e) {
 toast.error(e.response?.data?.detail || 'Failed to log progress');
 }
 }
 
 setDialogOpen(false);
 loadData(); // Reload to show updated status
 };

 const markDone = async (task) => {
 const isOffline = !navigator.onLine;
 const payload = { task_id: task.id, pct_complete: 100, qty_done: 'done', notes: 'Marked done via portal', photos: [] };
 if (isOffline) {
 const item = { id: 'q-' + crypto.randomUUID(), type: 'progress_log_v1', status: 'queued', createdAt: Date.now(), payload };
 await addQueueItem(item);
 toast.success('Queued: Task marked done (will sync when online)');
 return;
 }
 try {
 await axios.post('/progress-logs', payload);
 toast.success('Task marked done');
 loadData();
 } catch (e) {
 toast.error(e.response?.data?.detail || 'Failed');
 }
 };

 return (
 <div className="space-y-4">
 <h2 className="text-xl font-semibold">My Tasks</h2>
 {tasks.length === 0 ? (
 <div className="text-[#475569]">No tasks assigned</div>
 ) : (
 tasks.map((t) => (
 <Card key={t.id}>
 <CardContent className="p-4 flex items-center justify-between">
 <div>
 <div className="font-medium text-[#0F172A]">{t.name}</div>
 <div className="text-sm text-[#475569]">Status: {t.status}</div>
 </div>
 {t.needsLog && (
 <span className="ml-2 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-2 py-0.5">Needs log</span>
 )}

 <div className="flex gap-2">
 <Button onClick={() => openTaskDialog(t)} className="bg-blue-600 hover:bg-blue-700">Log Progress</Button>
 <Button onClick={() => markDone(t)} className="bg-green-600 hover:bg-green-700">Mark Done</Button>
 </div>
 </CardContent>
 </Card>
 ))
 )}
 
 <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
 <DialogContent className="sm:max-w-md">
 <DialogHeader>
 <DialogTitle>Log Progress - {selectedTask?.name}</DialogTitle>
 </DialogHeader>
 <div className="space-y-4">
 <div>
 <Label htmlFor="pct_complete">Percent Complete</Label>
 <Input
 id="pct_complete"
 type="number"
 min="0"
 max="100"
 value={progressData.pct_complete}
 onChange={(e) => setProgressData({...progressData, pct_complete: e.target.value})}
 placeholder="0-100"
 />
 </div>
 <div>
 <Label htmlFor="qty_done">Quantity Done</Label>
 <Input
 id="qty_done"
 value={progressData.qty_done}
 onChange={(e) => setProgressData({...progressData, qty_done: e.target.value})}
 placeholder="e.g., 5 units, 50 sq ft"
 />
 </div>
 <div>
 <Label htmlFor="notes">Notes</Label>
 <Textarea
 id="notes"
 value={progressData.notes}
 onChange={(e) => setProgressData({...progressData, notes: e.target.value})}
 placeholder="Progress notes..."
 rows={3}
 />
 </div>
 <div className="flex gap-2 justify-end">
 <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
 <Button onClick={submitProgress}>Submit Progress</Button>
 </div>
 </div>
 </DialogContent>
 </Dialog>
 </div>
 );
}
